/**
 * TcpaComplianceCard — Settings surface for the Autonomous Jarvis TCPA
 * attestation. This is the last gate on the AI SDR Readiness Checklist:
 * once the operator attests, `tcpa_consent_captured_in_forms` flips true
 * and Jarvis is legally cleared to auto-reply on inbound SMS.
 *
 * Design goals:
 *  - Show the EXACT consent language their forms display, dynamically
 *    branded with company name + persona name (so it's never generic).
 *  - Make the attestation feel legally serious — this is a signed
 *    statement, not a UI switch.
 *  - Show the attestation timestamp so both the operator and any auditor
 *    can see when it was recorded.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, Check, X, Sparkles } from "lucide-react";

export default function TcpaComplianceCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAttest, setPendingAttest] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/ai-sdr/compliance");
      setState(data);
      setPendingAttest(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load compliance state");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const attested = !!state?.attested;

  const save = async (nextAttested) => {
    if (nextAttested && !pendingAttest) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/v3/ai-sdr/compliance", { attested: nextAttested });
      setState(data);
      setPendingAttest(false);
      toast.success(
        nextAttested
          ? "TCPA attestation recorded. Jarvis is cleared to auto-reply."
          : "Attestation revoked. Jarvis will pause on all new inbound SMS.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save attestation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" data-testid="tcpa-compliance-card">
      <div className="flex items-start gap-3">
        <div
          className={
            "w-10 h-10 rounded-xl grid place-items-center shrink-0 " +
            (attested
              ? "bg-emerald-500/10 text-emerald-700"
              : "bg-amber-500/10 text-amber-700")
          }
        >
          {attested ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Sparkles size={11} /> TCPA · Jarvis Compliance
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mt-0.5">
            SMS consent on capture forms
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Jarvis will only auto-reply once you confirm every seller-facing
            capture form under your brand carries an SMS opt-in with STOP/HELP
            language and discloses that AI-drafted messages may be used.
          </p>
        </div>
        {attested && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 shrink-0">
            <Check className="w-3 h-3" /> Attested
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[var(--ink-3)]" data-testid="tcpa-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Verbatim consent snippet — mirrors what CaptureForm renders */}
          <div
            className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3"
            data-testid="tcpa-sample-language"
          >
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">
              What your forms will say
            </div>
            <div className="text-[12.5px] text-slate-700 leading-relaxed italic">
              &ldquo;{state?.sample_language}&rdquo;
            </div>
            <div className="text-[10.5px] text-slate-500 mt-2">
              Brand: <b>{state?.brand}</b> · Persona: <b>{state?.persona_name}</b>
              {" · "}Language updates automatically when you change either.
            </div>
          </div>

          {/* Attest / Revoke controls */}
          {!attested ? (
            <label
              className="mt-4 flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/50 cursor-pointer"
              data-testid="tcpa-attest-row"
            >
              <input
                type="checkbox"
                checked={pendingAttest}
                onChange={(e) => setPendingAttest(e.target.checked)}
                data-testid="tcpa-attest-checkbox"
                className="mt-0.5 w-4 h-4 shrink-0"
              />
              <span className="text-[12.5px] text-slate-800 leading-snug">
                I confirm that every seller-facing capture form under this brand
                &mdash; including my Marketplace, ebook landings, Property Command
                Center intake, and any embedded forms &mdash; displays the SMS
                opt-in language above and captures a checked box before storing
                the phone number.
              </span>
            </label>
          ) : (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-[12.5px] text-emerald-900" data-testid="tcpa-attested-row">
              Attested on{" "}
              <b>
                {state?.attested_at
                  ? state.attested_at.replace("T", " ").slice(0, 16)
                  : "—"}
              </b>
              . Jarvis is cleared to auto-reply on inbound SMS.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-4">
            {attested ? (
              <button
                type="button"
                onClick={() => save(false)}
                disabled={saving}
                data-testid="tcpa-revoke-btn"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-[12px] font-semibold text-slate-700 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                Revoke attestation
              </button>
            ) : (
              <button
                type="button"
                onClick={() => save(true)}
                disabled={saving || !pendingAttest}
                data-testid="tcpa-save-btn"
                className="inline-flex items-center gap-1 rounded-md text-white text-[12px] font-bold px-3 py-1.5 shadow-md hover:opacity-90 disabled:opacity-60"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                Record attestation
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
